import { getAuthToken } from '../../services/auth/tokenStorage';
import {
  generateValidationSalt,
  hashValidationAnswer,
  MIN_VALIDATION_ANSWER_LENGTH,
} from '../../services/auth/validationVerifier';
import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  TextInput,
  ScrollView,
  Modal,
  Pressable,
  Alert,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { NavigationProp, RouteProp } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../../config';

// Import icons
import backIcon from '../../assets/icons/back.png';
import infoIcon from '../../assets/icons/info.png';
import closeIcon from '../../assets/icons/close.png';

import { createLogger } from '../../utils/logger';
const logger = createLogger('AddKeyHolder');

interface AddKeyHolderScreenProps {
  navigation: NavigationProp<any>;
  route: RouteProp<{
    params: {
      prefilledData?: {
        email?: string;
        firstName?: string;
        lastName?: string;
        phone?: string;
        validationQuestion?: string;
      };
    };
  }, 'params'>;
}

const AddKeyHolderScreen: React.FC<AddKeyHolderScreenProps> = ({ navigation, route }) => {
  const prefilledData = route?.params?.prefilledData;

  const [firstName, setFirstName] = useState(prefilledData?.firstName || '');
  const [lastName, setLastName] = useState(prefilledData?.lastName || '');
  const [email, setEmail] = useState(prefilledData?.email || '');
  const [phone, setPhone] = useState(prefilledData?.phone || '');
  const [validationQuestion, setValidationQuestion] = useState(prefilledData?.validationQuestion || '');
  const [validationAnswer, setValidationAnswer] = useState('');
  const [showValidationInfo, setShowValidationInfo] = useState(false);
  const [loading, setLoading] = useState(false);

  // Format phone number as (xxx) xxx-xxxx
  const formatPhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (!match) return value;
    let formatted = '';
    if (match[1]) {
      formatted = `(${match[1]}`;
    }
    if (match[2]) {
      formatted += match[2].length === 3 ? `) ${match[2]}` : match[2];
    }
    if (match[3]) {
      formatted += match[3] ? `-${match[3]}` : '';
    }
    return formatted;
  };

  const handlePhoneChange = (value: string) => {
    setPhone(formatPhoneNumber(value));
  };

  const handleAddKeyholder = async () => {
    // Validation - all fields are now required
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !phone.trim()) {
      Alert.alert('Error', 'Please fill in all required fields (name, email, phone)');
      return;
    }

    if (!validationQuestion.trim() || !validationAnswer.trim()) {
      Alert.alert('Error', 'Please provide a validation question and answer');
      return;
    }

    if (validationAnswer.trim().length < MIN_VALIDATION_ANSWER_LENGTH) {
      Alert.alert(
        'Validation Answer Too Short',
        `Please enter an answer with at least ${MIN_VALIDATION_ANSWER_LENGTH} characters so your keyholder can match it.`,
      );
      return;
    }

    try {
      setLoading(true);

      const token = await getAuthToken();
      if (!token) {
        Alert.alert('Error', 'Please log in again');
        return;
      }

      // Create more limited access permissions for keyholders.
      // Documents are never a keyholder's (product rule, K4): the API
      // refuses them whatever is granted, so they are not promised here.
      const defaultAccessGrants = {
        'contacts': 'read',
        'personal_instructions': 'read',
        'final_wishes': 'read',
        // No access to financial records, medical info, passwords by default
      };

      // §10.2.1 — hash the validation answer client-side so the backend
      // never sees the plaintext. The invitee's client will recompute
      // the same verifier from the salt returned by verify-token.
      const validationAnswerSalt = await generateValidationSalt();
      const validationAnswerVerifier = await hashValidationAnswer(
        validationAnswer,
        validationAnswerSalt,
      );

      // SECURITY_AUDIT §10.2.2 — the keyholder flow used to ship a
      // sentinel 'KEYHOLDER_NO_PASSCODE' string to satisfy a non-null
      // backend constraint. Medium-term #2 made the field optional on the
      // backend DTO (keyholders now decrypt via their own KEK + a sealed
      // master key, never via a per-relationship passcode). Omit the
      // field entirely.
      const invitationData = {
        inviteeEmail: email.trim(),
        role: 'keyholder' as const,
        scopes: defaultAccessGrants,
        validationQuestion: validationQuestion.trim(),
        validationAnswerSalt,
        validationAnswerVerifier,
        phone: phone.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      };

      const response = await fetch(`${API_URL}/api/invitations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(invitationData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send invitation');
      }

      const data = await response.json();
      
      Alert.alert(
        'Invitation Sent!',
        `An invitation has been sent to ${email}. They will receive an email with instructions to join as your keyholder.`,
        [
          {
            text: 'OK',
            onPress: () => navigation.goBack()
          }
        ]
      );

    } catch (error) {
      logger.error('Error creating invitation:', error);
      Alert.alert(
        'Error', 
        error instanceof Error ? error.message : 'Failed to send invitation. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Image source={backIcon} style={styles.headerIcon} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Add Keyholder Details</Text>
        <View style={{ width: 24 }} />
      </View>

      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardAvoid}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 0}
      >
        <ScrollView
          style={styles.content}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.inputContainer}>
            <TextInput
              style={styles.input}
              placeholder="First Name"
              value={firstName}
              onChangeText={setFirstName}
              placeholderTextColor="#999"
            />
          </View>

          <View style={styles.inputContainer}>
            <TextInput
              style={styles.input}
              placeholder="Last Name"
              value={lastName}
              onChangeText={setLastName}
              placeholderTextColor="#999"
            />
          </View>

          <View style={styles.inputContainer}>
            <TextInput
              style={styles.input}
              placeholder="Email"
              value={email}
              onChangeText={setEmail}
              keyboardType="email-address"
              autoCapitalize="none"
              placeholderTextColor="#999"
            />
          </View>

          <View style={styles.inputContainer}>
            <TextInput
              style={styles.input}
              placeholder="Phone Number"
              value={phone}
              onChangeText={handlePhoneChange}
              keyboardType="phone-pad"
              placeholderTextColor="#999"
            />
          </View>

          <View style={styles.inputContainer}>
            <TextInput
              style={[styles.input, styles.validationInput]}
              placeholder="Keyholder Validation Question"
              value={validationQuestion}
              onChangeText={setValidationQuestion}
              placeholderTextColor="#999"
              multiline
              numberOfLines={3}
            />
            <TouchableOpacity
              style={styles.infoIcon}
              onPress={() => setShowValidationInfo(true)}
            >
              <Image source={infoIcon} style={styles.icon} />
            </TouchableOpacity>
          </View>
          <View style={styles.inputContainer}>
            <TextInput
              style={styles.input}
              placeholder="Validation Answer"
              value={validationAnswer}
              onChangeText={setValidationAnswer}
              placeholderTextColor="#999"
            />
          </View>
          <Text style={styles.answerHelperText}>
            Minimum {MIN_VALIDATION_ANSWER_LENGTH} characters
          </Text>

          <TouchableOpacity
            style={[
              styles.addButton,
              (!firstName || !lastName || !email || !phone || !validationQuestion || !validationAnswer || loading) && styles.disabledButton
            ]}
            onPress={handleAddKeyholder}
            disabled={!firstName || !lastName || !email || !phone || !validationQuestion || !validationAnswer || loading}
          >
            {loading ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <Text style={styles.addButtonText}>Send Invitation</Text>
            )}
          </TouchableOpacity>
        </ScrollView>
      </KeyboardAvoidingView>

      <Modal
        visible={showValidationInfo}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowValidationInfo(false)}
      >
        <Pressable 
          style={styles.modalOverlay}
          onPress={() => setShowValidationInfo(false)}
        >
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Validation Question for Your Keyholder</Text>
              <TouchableOpacity 
                onPress={() => setShowValidationInfo(false)}
                style={styles.closeButton}
              >
                <Image source={closeIcon} style={styles.closeIcon} />
              </TouchableOpacity>
            </View>
            <Text style={styles.modalText}>
              To help ensure your information is only shared with the right person, we require a validation question during setup.
            </Text>
            <Text style={styles.modalText}>
              This is a personal question and answer that only you and your chosen keyholder should know. When the time comes, your keyholder will need to correctly answer this question to link to your account and gain access.
            </Text>
            <Text style={styles.modalText}>
              <Text style={{ fontWeight: 'bold' }}>Important: </Text>
              This answer is not sent to them automatically. You'll need to share it with them directly and securely. Without it, they will not be able to access your information.
            </Text>
            <TouchableOpacity 
              style={styles.backButton}
              onPress={() => setShowValidationInfo(false)}
            >
              <Text style={styles.backButtonText}>Back to Keyholder Details</Text>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: ms(20),
    paddingVertical: ms(15),
    marginTop: ms(10),
    backgroundColor: '#F3F6F8',
  },
  headerIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  headerTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#333',
  },
  keyboardAvoid: {
    flex: 1,
  },
  content: {
    flex: 1,
    padding: ms(20),
  },
  scrollContent: {
    paddingBottom: ms(100),
  },
  inputContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    marginBottom: ms(15),
    flexDirection: 'row',
    alignItems: 'center',
    elevation: 2,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
  },
  input: {
    flex: 1,
    height: s(50),
    paddingHorizontal: ms(20),
    fontSize: ms(16, 0.3),
    color: '#333',
  },
  validationInput: {
    minHeight: s(70),
    textAlignVertical: 'top',
  },
  answerHelperText: {
    fontSize: ms(12, 0.3),
    color: '#6B7280',
    marginTop: ms(-10),
    marginBottom: ms(15),
    marginLeft: ms(6),
  },
  infoIcon: {
    padding: ms(10),
    marginRight: ms(10),
  },
  icon: {
    width: s(24),
    height: s(24),
    tintColor: '#999',
  },
  notificationSection: {
    marginTop: ms(20),
    marginBottom: ms(30),
  },
  sectionTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(15),
  },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: ms(15),
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    color: '#333',
  },
  addButton: {
    backgroundColor: '#29BFC0',
    height: s(50),
    borderRadius: ms(25),
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: ms(30),
    elevation: 2,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
  },
  disabledButton: {
    opacity: 0.6,
    elevation: 2,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    width: '85%',
    padding: ms(20),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: ms(15),
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#333',
    flex: 1,
  },
  closeButton: {
    padding: ms(5),
  },
  closeIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#333',
  },
  modalText: {
    fontSize: ms(14, 0.3),
    color: '#666',
    lineHeight: ms(20, 0.3),
    marginBottom: ms(15),
  },
  backButton: {
    backgroundColor: '#29BFC0',
    height: s(50),
    borderRadius: ms(25),
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: ms(10),
  },
  backButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
  },
});

export default AddKeyHolderScreen; 